import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Formik, Field } from "formik";
import * as Yup from "yup";
import { Form, Card, Row, Col } from "react-bootstrap";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import { authService } from "../../services/authService";
import ErrorIcon from "../../assets/images/fi_alert-triangle.svg";
import { GOOGLE_MAPS_API_KEY } from "../../config/ApiConfig";
import { toast } from "react-toastify";
import "../../styles/auth.css";

const validationSchema = Yup.object().shape({
  telephone: Yup.string()
    .required("Numéro de téléphone requis")
    .matches(/^(06|07)\d{8}$/, "Numéro de téléphone invalide (format: 06/07 + 8 chiffres)"),
  adresse: Yup.string().required("Adresse de facturation requise"),
  societe: Yup.string(),
  tva: Yup.string().matches(
    /^(FR\d{11})?$/,
    "TVA invalide (format: FR + 11 chiffres)"
  ),
  siret: Yup.string(),
});

const CompleteProfile = () => {
  const history = useHistory();
  const [error, setError] = useState("");
  const [isGMapsLoaded, setIsGMapsLoaded] = useState(false);
  const [address, setAddress] = useState("");
  const [latLong, setLatLong] = useState({});
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    // Récupérer les données temporaires de l'utilisateur Google
    const tempUserData = localStorage.getItem("tempGoogleUserData");
    if (!tempUserData) {
      // Si pas de données temporaires, rediriger vers login
      history.push("/login");
      return;
    }
    setUserData(JSON.parse(tempUserData));

    // Charger Google Maps
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
      if (existingScript) {
        const checkGoogle = setInterval(() => {
          if (window.google && window.google.maps && window.google.maps.places) {
            setIsGMapsLoaded(true);
            clearInterval(checkGoogle);
          }
        }, 100);
        return () => clearInterval(checkGoogle);
      } else {
        const gmapScriptEl = document.createElement("script");
        gmapScriptEl.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
        gmapScriptEl.async = true;
        gmapScriptEl.defer = true;
        gmapScriptEl.onload = () => setIsGMapsLoaded(true);
        gmapScriptEl.onerror = () => {
          console.error("Erreur de chargement de Google Maps");
          setIsGMapsLoaded(false);
        };
        document.body.appendChild(gmapScriptEl);
      }
    } else {
      setIsGMapsLoaded(true);
    }
  }, [history]);

  const handleChangeAddress = (newAddress, setFieldValue) => {
    setAddress(newAddress);
    setFieldValue("adresse", newAddress);
  };

  const handleSelectAddress = async (selectedAddress, setFieldValue) => {
    setAddress(selectedAddress);
    try {
      const results = await geocodeByAddress(selectedAddress);
      const latLng = await getLatLng(results[0]);
      setLatLong(latLng);
      setFieldValue("adresse", selectedAddress, true);
      setFieldValue("latitude", latLng.lat);
      setFieldValue("longitude", latLng.lng);
    } catch (error) {
      console.error("Erreur lors du géocodage :", error);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setError("");

      // Déterminer si c'est un particulier (1) ou une entreprise (2)
      const isEntreprise = (values.societe && values.societe.trim() !== "") ||
                           (values.siret && values.siret.trim() !== "");

      const data = {
        user_id: userData.id,
        telephone: values.telephone,
        adresse: values.adresse,
        latitude: latLong.lat,
        longitude: latLong.lng,
        role: isEntreprise ? 2 : 1, // 1=Particulier, 2=Entreprise
      };

      // Si entreprise, ajouter les champs obligatoires
      if (isEntreprise) {
        data.societe = values.societe || "";
        data.siret = values.siret || "";
        if (values.tva && values.tva.trim() !== "") {
          data.tva = values.tva;
        }
      }

      // Envoyer les données au backend pour compléter le profil
      await authService.completeProfile(data);

      // Nettoyer les données temporaires
      localStorage.removeItem("tempGoogleUserData");

      toast.success("Profil complété avec succès !");
      setTimeout(() => {
        window.location.href = "/commande";
      }, 100);
    } catch (err) {
      setError(
        err.response?.data?.message ||
        "Une erreur est survenue lors de la complétion du profil. Veuillez réessayer."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const searchOptions = {
    componentRestrictions: { country: ["fr", "mc"] },
    types: ["geocode", "establishment"],
  };

  if (!userData) {
    return null;
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <Card>
          <Card.Body className="p-5">
            <h2 className="text-center mb-4">Complète ton profil</h2>
            <p className="text-center text-muted mb-4">
              Bienvenue {userData.prenom} ! Complète ton profil pour finaliser ton inscription
            </p>

            {error && (
              <div className="alert alert-danger d-flex align-items-center" role="alert">
                <img src={ErrorIcon} className="me-2" alt="error" width="20" />
                <span>{error}</span>
              </div>
            )}

            <Formik
              initialValues={{
                telephone: "",
                adresse: "",
                latitude: "",
                longitude: "",
                societe: "",
                tva: "",
                siret: "",
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleSubmit,
                setFieldValue,
                isSubmitting,
              }) => (
                <Form onSubmit={handleSubmit}>
                  <h5 className="mb-3 text-primary">Informations de contact</h5>

                  <Row>
                    <Col md={6}>
                      <div className="form-floating mb-3">
                        <Field
                          type="tel"
                          name="telephone"
                          className={`form-control ${
                            touched.telephone && errors.telephone ? "is-invalid" : ""
                          }`}
                          placeholder="Téléphone"
                        />
                        <label>Numéro de téléphone *</label>
                        {touched.telephone && errors.telephone && (
                          <div className="text-danger mt-1 small d-flex align-items-center">
                            <img src={ErrorIcon} className="me-1" alt="error" width="16" />
                            {errors.telephone}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col md={6}>
                      {isGMapsLoaded ? (
                        <PlacesAutocomplete
                          value={address}
                          onChange={(newValue) =>
                            handleChangeAddress(newValue, setFieldValue)
                          }
                          onSelect={(selected) =>
                            handleSelectAddress(selected, setFieldValue)
                          }
                          searchOptions={searchOptions}
                        >
                          {({
                            getInputProps,
                            suggestions,
                            getSuggestionItemProps,
                            loading,
                          }) => (
                            <div className="form-floating mb-3 position-relative">
                              <input
                                {...getInputProps({
                                  placeholder: " ",
                                  id: "adresse",
                                  className: `form-control ${
                                    touched.adresse && errors.adresse
                                      ? "is-invalid"
                                      : ""
                                  }`,
                                })}
                              />
                              <label htmlFor="adresse">Adresse de facturation *</label>
                              {suggestions.length > 0 && (
                                <div className="position-absolute w-100 bg-white border rounded shadow-sm" style={{ zIndex: 1000, top: "100%", maxHeight: "200px", overflowY: "auto" }}>
                                  {loading && <div className="p-2">Chargement...</div>}
                                  {suggestions.map((suggestion) => (
                                    <div
                                      {...getSuggestionItemProps(suggestion, {
                                        className: "p-2 cursor-pointer hover-bg-light",
                                        style: {
                                          backgroundColor: suggestion.active
                                            ? "#e9ecef"
                                            : "#fff",
                                          cursor: "pointer",
                                        },
                                      })}
                                      key={suggestion.placeId}
                                    >
                                      {suggestion.description}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {touched.adresse && errors.adresse && (
                                <div className="text-danger mt-1 small d-flex align-items-center">
                                  <img src={ErrorIcon} className="me-1" alt="error" width="16" />
                                  {errors.adresse}
                                </div>
                              )}
                            </div>
                          )}
                        </PlacesAutocomplete>
                      ) : (
                        <div className="form-floating mb-3">
                          <Field
                            type="text"
                            name="adresse"
                            id="adresse"
                            className={`form-control ${
                              touched.adresse && errors.adresse ? "is-invalid" : ""
                            }`}
                            placeholder=" "
                          />
                          <label htmlFor="adresse">Adresse de facturation *</label>
                          {touched.adresse && errors.adresse && (
                            <div className="text-danger mt-1 small d-flex align-items-center">
                              <img src={ErrorIcon} className="me-1" alt="error" width="16" />
                              {errors.adresse}
                            </div>
                          )}
                        </div>
                      )}
                    </Col>
                  </Row>

                  <h5 className="mb-3 text-primary mt-4">Informations entreprise (facultatif)</h5>

                  <Row>
                    <Col md={6}>
                      <div className="form-floating mb-3">
                        <Field
                          type="text"
                          name="societe"
                          className="form-control"
                          placeholder="Société"
                        />
                        <label>Nom de la société</label>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="form-floating mb-3">
                        <Field
                          type="text"
                          name="tva"
                          className={`form-control ${
                            touched.tva && errors.tva ? "is-invalid" : ""
                          }`}
                          placeholder="TVA"
                        />
                        <label>TVA (ex: FR12345678901)</label>
                        {touched.tva && errors.tva && (
                          <div className="text-danger mt-1 small d-flex align-items-center">
                            <img src={ErrorIcon} className="me-1" alt="error" width="16" />
                            {errors.tva}
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <div className="form-floating mb-3">
                        <Field
                          type="text"
                          name="siret"
                          className="form-control"
                          placeholder="SIREN"
                        />
                        <label>SIREN</label>
                      </div>
                    </Col>
                  </Row>

                  <div className="d-grid gap-2 mt-4">
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Enregistrement en cours..." : "Finaliser mon inscription"}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default CompleteProfile;
